import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import DateTimePicker from "src/components/DateTimePicker";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import isValidDate from "src/utils/isValidDate";
import { IRefActionsModalShareIndicators } from "./interface";
import schema from "./schemas";

const ModalIndicatorShare: React.ForwardRefRenderFunction<IRefActionsModalShareIndicators> = (_, ref) => {
	const { translate } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();

	// Modal Open Share Indicators
	const modalShareIndicator = useRef<RCCRefModalProps>(null);
	const indicatorsId = useRef<string>("");
	const linkRef = useRef<HTMLSpanElement>(null);
	const [linkGenerated, setLinkGenerated] = useState("");
	const [showSaveButton, setShowSaveButton] = useState(true);
	const [showLink, setShowLink] = useState(false);
	const { handleSubmit, control, formState:{ errors }, setError, clearErrors } = useForm();
	const actionButtonOpenShareIndicators = async (indicators: string) => {
		if(indicators.length === 0){
			addToast({
				title: translate("Erro"),
				description: translate("Selecione ao menos um indicador para compartilhar"),
				color: "yellow"
			});
			return;
		}
		try{
			setLoading(true);
						
			indicatorsId.current = indicators;
			setShowSaveButton(true);
			setShowLink(false);
			setLoading(false);
			modalShareIndicator.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os detalhes dos indicadores"), addToast);
			setLoading(false);
		}
	};
	const onSubmit = async (data: any) => {
		try{
			clearErrors();
			modalShareIndicator.current?.setLoadingButton();

			await schema(translate).validate({ ...data }, {
				abortEarly: false
			});
			
			let baseYearVar = data.baseYear;
			if(typeof data.baseYear !== "string")
				baseYearVar = !isValidDate(data.baseYear) ? new Date().getFullYear().toString() : data.baseYear.getFullYear().toString();

			const response = await RCCServicesApi.postRequest({
				method: "Indicators/GenerateKeyToShareIndicators",
				body: {
					date: formatDate(data.date),
					baseYear: baseYearVar,
					indicatorsId: indicatorsId.current
				}
			});

			const splitHRef = window.location.href.split("/");
			const path = "indicators/presentation/selected";
			setLinkGenerated(`${splitHRef[0]}//${splitHRef[2]}/${path}/key=${response.data}`);
			
			addToast({
				title: translate("Sucesso"),
				description: translate("Link gerado com sucesso"),
				color: "green"
			});
			setShowSaveButton(false);
			setShowLink(true);
			modalShareIndicator.current?.resetLoadingButton();
		}catch(err){
			modalShareIndicator.current?.resetLoadingButton();
			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, translate("Erro ao gerar link de compartilhamento"), addToast);	
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonOpenShareIndicators
	}));
    

	return (
		<>		
			{/* Modal Share INDICATOR */}
			<Modal
				ref={modalShareIndicator}
				header={{
					title: translate("Compartilhar indicadores"),
					icon: {
						name: "linkExternal"
					}
				}}
				footer= {{
					showSaveButton,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				onSubmit={handleSubmit(onSubmit)}
				styleInline={`${ModalSide} height: 30%; min-height: 300px;`}
			>
				<RCCGrid container item direction="column">
					<DateTimePicker 
						type="datePicker"
						label=""
						name="today"
						defaultValue={new Date().setHours(0, 0, 0, 0)}	
						control={control}
						styleInline="display: none;"
						required
						props={{datePicker: {
							disabled: true
						}}}
					/>
					<RCCGrid container item spacing={1}>
						<RCCGrid container item xs={12} sm={6}>
							<DateTimePicker 
								type="datePicker"
								label={translate("Válido até")}
								name="date"
								defaultValue={new Date()}	
								control={control}
								error={errors.date}
								styleInline="margin: 5px 0;"
								required
							/>
						</RCCGrid>
						<RCCGrid container item xs={12} sm={6} alignItems="center">
							<DateTimePicker
								type="datePicker" 
								control={control}
								name="baseYear"
								label={translate("Ano base")}
								defaultValue={new Date()}
								props={{
									datePicker: {
										format: "yyyy",
										views: ["year"]
									}
								}}
							/>		
						</RCCGrid>
					</RCCGrid>


					{showLink && (
						<RCCGrid container item>
							<Title color="purple" size="sm" styleInline="max-width: 100%;">
								<span ref={linkRef} style={{
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									maxWidth: "100%"
								}} title={linkGenerated}>
									{linkGenerated}
								</span>
							</Title>
							<RCCButton 
								color="purple"
								icon={{
									size: 16,
									name: "copy",
									color: "#FFF"
								}}
								layout="isRounded"
								onClick={() => {
									if(linkRef === null || !linkRef.current?.textContent)
										return;

									addToast({
										color: "blue",
										description: translate("Texto copiado"),
										title: translate("Sucesso")
									});
									navigator.clipboard.writeText(linkRef.current?.textContent);
								}}
							/>
						</RCCGrid>
					)}

				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalIndicatorShare);
